import type { NumberBinding } from 'shared-types';
import { T, useT } from '../../../translation/src';
import { NumberField, SelectField } from '../panels/fields';

const CONSTANT = '__value';
const FORMULA = '__formula';

/** A number that is typed in, follows a parameter, or is a formula. */
export const BindingField: React.FC<{
  binding: NumberBinding | undefined;
  params: ReadonlyArray<{ key: string; label: string }>;
  onChange: (binding: NumberBinding | undefined) => void;
  unit?: string;
  integer?: boolean;
  fallback: number;
  allowNone?: string;
  minValue?: number;
}> = ({ binding, params, onChange, unit, integer, fallback, allowNone, minValue }) => {
  const fixedLabel = useT('fixed value', { swc: true });
  const formulaLabel = useT('by a rule', { swc: true });
  const mode = !binding ? '' : 'param' in binding ? binding.param : 'expr' in binding ? FORMULA : CONSTANT;
  const options = [
    ...(allowNone ? [{ value: '', label: allowNone }] : []),
    { value: CONSTANT, label: fixedLabel },
    ...params.map((p) => ({ value: p.key, label: p.label })),
    // Formulas come from pinned points and the AI: shown as what they are, never offered to type.
    ...(mode === FORMULA ? [{ value: FORMULA, label: formulaLabel }] : []),
  ];
  const pick = (v: string): NumberBinding | undefined => {
    if (v === '') return undefined;
    if (v === CONSTANT) return { value: binding && 'value' in binding ? binding.value : fallback };
    if (v === FORMULA) return { expr: binding && 'expr' in binding ? binding.expr : binding && 'param' in binding ? binding.param : String(fallback) };
    return { param: v };
  };
  return (
    <span className="flex flex-col gap-1">
      <SelectField value={mode} options={options} onChange={(v) => onChange(pick(v))} />
      {binding && 'value' in binding && <NumberField value={binding.value} unit={unit} integer={integer} min={minValue ?? (integer ? 1 : 0.001)} onCommit={(value) => onChange({ value })} />}
      {binding && 'expr' in binding && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          <T _str="set by a rule" swc />
        </span>
      )}
    </span>
  );
};
